import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { motionOn } from '../../lib/motion.ts'
import { lockScroll } from '../../lib/scroll.ts'
import { TODAY_KEY } from '../home/today.ts'
import { GARDEN } from './Garden.tsx'
import { refreshPerjalanan, type Garden } from './perjalanan.ts'

const SPARKS = Array.from({ length: 16 }, (_, i) => {
  const a = (i / 16) * Math.PI * 2
  const d = 70 + (i % 3) * 26
  return { dx: Math.round(Math.cos(a) * d), dy: Math.round(Math.sin(a) * d), delay: 500 + (i % 5) * 50 }
})

/**
 * "Naik tingkat" (docs/UX-PLAYBOOK.md §8): the old garden crossfades into the new one with sparks, 1.6 s; static with
 * reduced motion. A dialog the user closes.
 */
export function LevelUp({ from, to, onClose }: { from: number; to: number; onClose: () => void }) {
  const btn = useRef<HTMLButtonElement>(null)
  const [moving] = useState(motionOn)
  useEffect(() => lockScroll(), [])
  useEffect(() => {
    btn.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  const img = (n: number) => `/img/garden/lv${n}`
  return (
    <div className="levelup" role="dialog" aria-modal="true" aria-labelledby="lvup-t" data-moving={moving ? '' : undefined}>
      <div className="lvup-stage">
        <img className="lvup-old" src={`${img(from)}.webp`} alt="" width={320} height={320} />
        <img className="lvup-new" src={`${img(to)}.webp`} srcSet={`${img(to)}.webp 640w, ${img(to)}@2x.webp 1280w`} sizes="320px" alt={`Tamanmu sekarang: ${GARDEN[to - 1]}`} width={320} height={320} />
        {moving && (
          <div className="sparks" aria-hidden="true" style={{ left: '50%', top: '48%' }}>
            {SPARKS.map((s, i) => <i key={i} className="spark" style={{ '--dx': `${s.dx}px`, '--dy': `${s.dy}px`, animationDelay: `${s.delay}ms` } as CSSProperties} />)}
          </div>
        )}
      </div>
      <p className="eyebrow">Tingkat {to} dari {GARDEN.length}</p>
      <h2 id="lvup-t">{GARDEN[to - 1]}</h2>
      <p className="lvup-sub">Tamanmu tumbuh. Cahaya ini penanda langkahmu, bukan hitungan pahala.</p>
      <button ref={btn} type="button" className="btn btn-primary" onClick={onClose}>Lanjutkan</button>
    </div>
  )
}

/**
 * After an action that gives light: refresh Perjalanan (and Home, which shows the same light), and show "naik tingkat"
 * when the garden crossed a level.
 */
export function useGarden() {
  const qc = useQueryClient()
  const [up, setUp] = useState<Garden['levelUp']>(null)
  const after = (g: Garden) => {
    if (g.levelUp) setUp(g.levelUp)
    void refreshPerjalanan(qc)
    void qc.invalidateQueries({ queryKey: TODAY_KEY })
  }
  const overlay = up ? <LevelUp from={up.from} to={up.to} onClose={() => setUp(null)} /> : null
  return { after, overlay }
}
